/** KeyWin OnPrem strip: 14 hooks. Number of strips is dynamic (1, 2, 4, …). */
export const DEFAULT_SLOTS_PER_LIST = 14;

export type CabinetKeyPosition = { slot?: number | null; list?: number | null };

export function listIndexForKey(
  key: CabinetKeyPosition,
  slotsPerList: number
): number | null {
  if (key.slot == null || key.slot <= 0) {
    return null;
  }
  if (key.list != null && key.list > 0) {
    return key.list - 1;
  }
  return Math.floor((key.slot - 1) / slotsPerList);
}

/** Hook 1–14 on its own strip. OnPrem keys keep per-list Position; cloud uses a flat Slot. */
export function hookOnList(key: CabinetKeyPosition, slotsPerList: number): number | null {
  if (key.slot == null || key.slot <= 0) {
    return null;
  }
  if (key.list != null && key.list > 0 && key.slot <= slotsPerList) {
    return key.slot;
  }
  return ((key.slot - 1) % slotsPerList) + 1;
}

export function cabinetListCount(
  cabinetCapacity: number,
  reportedListCount: number | undefined,
  keys: CabinetKeyPosition[],
  slotsPerList: number
): number {
  let maxList = 0;
  for (const key of keys) {
    const idx = listIndexForKey(key, slotsPerList);
    if (idx != null && idx + 1 > maxList) {
      maxList = idx + 1;
    }
  }
  const fromCapacity = cabinetCapacity > 0 ? Math.ceil(cabinetCapacity / slotsPerList) : 0;
  return Math.max(1, reportedListCount ?? 0, fromCapacity, maxList);
}

export function effectiveCabinetCapacity(
  cabinetCapacity: number,
  keys: CabinetKeyPosition[],
  slotsPerList: number,
  reportedListCount?: number
): number {
  const lists = cabinetListCount(cabinetCapacity, reportedListCount, keys, slotsPerList);
  const maxSlot = keys.reduce((max, key) => {
    if (key.slot != null && key.slot > max) {
      return key.slot;
    }
    return max;
  }, 0);

  return Math.max(cabinetCapacity || 0, maxSlot, lists * slotsPerList, slotsPerList);
}

/** Place keys on their real hooks. Keys without slot are omitted — never packed 1..N. */
export function splitIntoLists<T extends CabinetKeyPosition>(
  keys: T[],
  capacity: number,
  slotsPerList: number = DEFAULT_SLOTS_PER_LIST,
  reportedListCount?: number
): T[][] {
  const numLists = cabinetListCount(capacity, reportedListCount, keys, slotsPerList);
  const lists: T[][] = Array.from({ length: numLists }, () => []);

  for (const key of keys) {
    const listIdx = listIndexForKey(key, slotsPerList);
    if (listIdx == null || listIdx < 0 || listIdx >= numLists) {
      continue;
    }
    lists[listIdx].push(key);
  }
  return lists;
}
